/**
 * @file index.tsx
 *
 * @fileoverview Renders a featured section with 2 columns icon text combination.
 */

import React, { useMemo } from 'react';
import { FeatureCentered2ColumnsParagraph } from '~/jobiqo/types/types';
import dynamic from 'next/dynamic';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { Box } from '~/vcl/atoms/07-box';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Grid } from '~/vcl/atoms/06-layout/04-grid';
import { Cell } from '~/vcl/atoms/06-layout/04-grid/01-cell';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { getFrontendTheme } from '~/jobiqo/utils/getFrontend';
import { Badge } from '~/vcl/atoms/03-badge';
import { MenuIcon } from '~/vcl/icons/Menu';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

interface FeatureCentered2ColumnsProps {
  row: FeatureCentered2ColumnsParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}
export const FeatureCentered2Columns = ({
  row,
  maxContainerSize,
}: FeatureCentered2ColumnsProps) => {
  const frontendConfig = useFrontendConfig();
  const frontendTheme = getFrontendTheme(frontendConfig);
  const LandingPageIcon = useMemo(
    () =>
      dynamic(() => import('~/jobiqo/components/Landingpage/LandingPageIcon'), {
        ssr: false,
        loading: () => (
          <MenuIcon
            color={
              frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.ICON_COLOR
            }
            height={
              frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.ICON_SIZE
            }
            width={
              frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.ICON_SIZE
            }
          />
        ),
      }),
    [frontendTheme],
  );

  const ParagraphInfo = ({ children }) =>
    frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.INTRO_BADGE ? (
      <Badge>{children}</Badge>
    ) : (
      <Text
        marginTop="0"
        textAlign={
          frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.TEXT_ALIGN
        }
        color={row.color as any}
        fontWeight="bold"
        textTransform="uppercase"
      >
        {children}
      </Text>
    );

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      marginTop={0}
      paddingTop={row.spacing ?? 11}
      paddingBottom={row.spacing ?? 12}
      backgroundColorOverride={
        row.background ? `var(--color-${row.background})` : `var(--color-white)`
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        {row.featuredSubTitle && (
          <>
            <ParagraphInfo>{row.featuredSubTitle}</ParagraphInfo>
            <Spacing size="medium" />
          </>
        )}
        {row.featuredTitle && (
          <>
            <Text
              textAlign={
                frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.TEXT_ALIGN
              }
              tag="h2"
              fontSize={[5, 7]}
              lineHeight={[5, 7]}
            >
              {row.featuredTitle}
            </Text>
            <Spacing size="large" />
          </>
        )}
        {row.text && (
          <Text
            textAlign={
              frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.TEXT_ALIGN
            }
            color="gray6"
            fontSize={4}
            lineHeight={5}
          >
            {row.text}
          </Text>
        )}
        <Spacing marginBottom={11} />
        <Grid columns={[1, 2]}>
          {row.items?.map((item, index) => {
            return (
              item && (
                <Cell key={index}>
                  <FlexItems alignItems="flex-start" gap={6} flexWrap="nowrap">
                    {(item.icon || item.iconCustom) && (
                      <FlexItem>
                        <Box
                          marginTop={0}
                          paddingTop={6}
                          paddingBottom={5}
                          paddingLeft={6}
                          paddingRight={6}
                          backgroundColorOverride={
                            frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS
                              .ICON_BOX_COLOR
                              ? `var(--color-${frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS.ICON_BOX_COLOR})`
                              : `var(--color-${row.color})`
                          }
                          {...(frontendTheme.LANDING_PAGES
                            .FEATURE_CENTERED_2_COLS.ICON_BOX_SHADOW
                            ? {
                                boxShadow:
                                  frontendTheme.LANDING_PAGES
                                    .FEATURE_CENTERED_2_COLS.ICON_BOX_SHADOW,
                              }
                            : null)}
                          {...(frontendTheme.LANDING_PAGES
                            .FEATURE_CENTERED_2_COLS.ICON_BOX_RADIUS
                            ? {
                                borderRadius:
                                  frontendTheme.LANDING_PAGES
                                    .FEATURE_CENTERED_2_COLS.ICON_BOX_RADIUS,
                              }
                            : null)}
                        >
                          <LandingPageIcon
                            icon={item?.icon}
                            iconCustom={item?.iconCustom}
                            color={
                              frontendTheme.LANDING_PAGES
                                .FEATURE_CENTERED_2_COLS.ICON_COLOR
                            }
                            height={
                              frontendTheme.LANDING_PAGES
                                .FEATURE_CENTERED_2_COLS.ICON_SIZE
                            }
                            width={
                              frontendTheme.LANDING_PAGES
                                .FEATURE_CENTERED_2_COLS.ICON_SIZE
                            }
                          />
                        </Box>
                      </FlexItem>
                    )}
                    <FlexItem>
                      <Text
                        tag="h3"
                        fontWeight={
                          frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS
                            .ICON_TITLE_WEIGHT
                        }
                        fontSize={[4, 5]}
                        {...(frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS
                          .ICON_TITLE_COLOR
                          ? {
                              color:
                                frontendTheme.LANDING_PAGES
                                  .FEATURE_CENTERED_2_COLS.ICON_TITLE_COLOR,
                            }
                          : null)}
                      >
                        {item.title}
                      </Text>
                      <Text
                        color={
                          frontendTheme.LANDING_PAGES.FEATURE_CENTERED_2_COLS
                            .ICON_TEXT_COLOR
                        }
                      >
                        {item.text}
                      </Text>
                      <Spacing size="medium" />
                    </FlexItem>
                  </FlexItems>
                </Cell>
              )
            );
          })}
        </Grid>
      </Main>
    </Box>
  );
};
export default FeatureCentered2Columns;
